/**
 * "Open guest page" (admin only): opens, in a new tab, the page one guest
 * sees, through a 10-minute admin view link of its own
 * (getAdminGuestPageLink; functions/src/services/delivery.ts
 * mintAdminGuestView). The guest's own link is never read or shown, nothing
 * is emailed, and the page opened that way changes nothing of the guest's (no
 * first open, no download, no dates). The server records who opened which
 * page and when. Used on the Review tab's preview and the Captures tab's lines.
 */
import React, { useState } from "react";
import { rigv2 } from "./rigv2Callables.js";

export const GUEST_PAGE_LABEL = "Open guest page";
// The only place an admin view link may open: the viewer's /a/<token> route
// (functions/src/services/delivery.ts ADMIN_VIEW_LINK_BASE).
export const VIEWER_ORIGIN = "https://nyc-splat-viewer.vercel.app";

/** The link if it is exactly an admin view link on the viewer (no other host, no user, query or fragment), else "". */
export const adminViewUrl = (raw) => {
  if (typeof raw !== "string") return "";
  let u;
  try {
    u = new URL(raw);
  } catch {
    return "";
  }
  const exact = u.origin === VIEWER_ORIGIN && !u.username && !u.password && !u.search && !u.hash
    && /^\/a\/[a-f0-9]{32}$/.test(u.pathname) && raw === `${VIEWER_ORIGIN}${u.pathname}`;
  return exact ? raw : "";
};
export const POPUP_NOTE = "If no new tab opened, allow pop-ups for nycsuite.io and try again. The link lasts 10 minutes.";

/** Whether a member's delivery line has a page to open: an email may have reached them (even if the page ended since). */
export const hasGuestPage = (m) => Boolean(m?.rosterEntryId && m.emailedAt);

/** The callable's refusal in plain words. */
export const guestPageErrorText = (e) => {
  const code = String(e?.message || "");
  if (code === "no_page") return "This guest has no page yet: nothing was emailed to them.";
  if (/Requires role/.test(code)) return "Only an admin can open a guest's page.";
  return `Could not open the guest page (${code || "no answer"}). Try again.`;
};

/**
 * The click, pure given its parts: a blank tab opened at once (inside the
 * click, so the browser does not block it), cut off from this page, then sent
 * to the link once it comes. If the link does not come, the tab closes and
 * the words say why. Returns { opened, note }.
 */
export const runOpenGuestPage = async ({ getLink, openWindow, jobId, rosterEntryId }) => {
  let tab;
  try {
    tab = openWindow("", "_blank");
  } catch {
    tab = null;
  }
  if (tab) {
    try { tab.opener = null; } catch { /* already cut off */ }
  }
  let r;
  try {
    r = await getLink({ jobId, rosterEntryId });
  } catch (e) {
    if (tab) try { tab.close(); } catch { /* gone */ }
    return { opened: false, note: guestPageErrorText(e) };
  }
  const url = adminViewUrl(r?.url);
  if (!url) {
    if (tab) try { tab.close(); } catch { /* gone */ }
    return { opened: false, note: guestPageErrorText(null) };
  }
  if (tab && !tab.closed) {
    tab.location.replace(url);
    return { opened: true, note: "" };
  }
  // The blank tab was blocked or closed: one more try, without a way back to
  // this page (a tab opened so gives no answer to say whether it opened).
  try { openWindow(url, "_blank", "noopener,noreferrer"); } catch { /* blocked */ }
  return { opened: true, note: POPUP_NOTE };
};

/** The button, with its note under it. name: the guest's name when the capture has several people. */
export default function GuestPageButton({
  jobId, rosterEntryId, name = "", getLink = rigv2.getAdminGuestPageLink, openWindow = (...a) => window.open(...a),
}) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const click = async () => {
    if (busy) return;
    setBusy(true);
    setNote("");
    try {
      const r = await runOpenGuestPage({ getLink, openWindow, jobId, rosterEntryId });
      setNote(r.note);
    } finally {
      setBusy(false);
    }
  };
  const label = name ? `${GUEST_PAGE_LABEL}: ${name}` : GUEST_PAGE_LABEL;
  return (
    <>
      <button type="button" className="rigv2-tile-btn" disabled={busy} onClick={click}
        title="What this guest sees, in a new tab. Admin view: it changes nothing of theirs and emails nothing.">
        {busy ? "Opening…" : label}
      </button>
      {note && <span className="rigv2-dlv-note">{note}</span>}
    </>
  );
}
